import BaseService from './BaseService';
import MapService from "./MapService";
import GeoService from "./GeoService";
import DataService from "./DataService";
import NetworkService from "./NetworkService";
import {dataTypes, featureTypes} from "./Constants";
import CityList from "../data/CityList";
import HotelList from "../data/HotelList";
import POIList from "../data/POIList";
import CityData from "../data/CityData";
import StageData from "../data/StageData";
import OfflineCard from "../data/OfflineCard";
// import GeoBounds from "../data/GeoBounds";
import type LocationData from "../data/LocationData";
import type PlaceData from "../data/PlaceData";
import type CardData from "../data/CardData";
import type GeoBounds from "../data/GeoBounds";
import type BaseList from "../data/BaseList";
import type FeatureData from "../data/FeatureData";
import type HotelData from "../data/HotelData";
import type POIData from "../data/POIData";
import type { LngLatBounds, MapboxGeoJSONFeature } from 'mapbox-gl';

/**
 * Service / Manager for data linked to Map.
 * - Handles selected and displayed features.
 * - Gets Card data from map features.
 * - Retrieves data objects from DataService.
 */
class MapData extends BaseService {
    // Store inital location and place data
    static initLocation: LocationData;
    static initPlaceData: PlaceData | false;

    // Store last place selected.
    // @var {PlaceData}
    static lastPlaceData: PlaceData | null;
    static lastCardData: CardData | null;

    /**
     * Selected card data.
     *
     * @var {CardData}
     */
    static selectedCard: CardData | null;

    /**
     * @var {HotelList} Hotel list
     */
    static hotelList: HotelList;

    /**
     * @var {POIList} POI list
     */
    static POIList: POIList;

    /**
     * @var {CityList} City list
     */
    static cityList: CityList;

    /**
     * @var {LngLatBounds} Bounds for this list
     */
    static mapBounds: GeoBounds | null;

    /**
     * Map zoom level. Used to control element visibility.
     */
    static zoom: number;

    // These will depend on zoom
    // @var {boolean}
    static showHotels: boolean;
    static showPlaces: boolean;
    static showStages: boolean;
    static showRoutes: boolean;
    static showArrows: boolean;
    static showPOIs: boolean;

    /**
     * Build from data Items.
     */
    static init(): void {
        this.cityList = new CityList();
        this.hotelList = new HotelList();
        this.POIList = new POIList();
        DataService.init();

        this.initLocation = GeoService.getInitialPosition();
        this.debug('Initial Location', this.initLocation);

        this.initPlaceData = GeoService.getPlaceFromUrl();

        if (this.initPlaceData) {
            this.debug('Initial Place', this.initPlaceData);
        }
    }

    /**
     * Set visibility from zoom level.
     *
     * @param {float} zoom
     */
    static setVisibility(zoom: number): void {
        this.zoom = zoom;
        this.showHotels = GeoService.showHotels(zoom);
        this.showPlaces = GeoService.showPlaces(zoom);
        this.showStages = GeoService.showStages(zoom);
        this.showRoutes = GeoService.showRoutes(zoom);
        this.showArrows = GeoService.showArrows(zoom);
        this.showPOIs = GeoService.showHotels(zoom);
    }

    /**
     * Get initial place data, if any.
     */
    static getInitLocation(): LocationData {
        return this.initLocation;
    }

    /**
     * Get initial place data, if any.
     */
    static getInitPlaceData(): PlaceData | false {
        return this.initPlaceData;
    }

    /**
     * Gets city from map feature.
     */
    static getCityFromMapFeature(feature: MapboxGeoJSONFeature): CityData {
        // La capa del feature determina su forma; el runtime la garantiza.
        return CityData.fromMapFeature(feature as unknown as Parameters<typeof CityData.fromMapFeature>[0]);
    }

    /**
     * Gets Route from map feature.
     */
    static getRouteFromMapFeature(feature: MapboxGeoJSONFeature) {
        if (feature.properties!.route_id) {
            return DataService.getRouteByKey(feature.properties!.route_id);
        }
    }

    /**
     * Gets Stage from map feature.
     */
    static getStageFromMapFeature(feature: MapboxGeoJSONFeature) {
        const routeData = this.getRouteFromMapFeature(feature);
        if (routeData) {
            const stageData = StageData.fromMapFeature(feature as unknown as Parameters<typeof StageData.fromMapFeature>[0]);
            stageData.routeData = routeData;
            return stageData;
        }
    }

    /**
     * Translates selected feature to feature data.
     * Takes into account curren zoom and visibility.
     *
     * @param {string} type
     * @param {object} feature
     *
     * @return {FeatureData}|null
     */
    static getSelectedFeatureData(type: string, feature: MapboxGeoJSONFeature) {
        switch (type) {
            case featureTypes.localidad:
                return this.showPlaces && MapData.getCityFromMapFeature(feature);
            // break;

            case featureTypes.track:
                if (this.showStages) {
                    return MapData.getStageFromMapFeature(feature);
                }
                else if (this.showRoutes) {
                    return MapData.getRouteFromMapFeature(feature);
                }
                break;

            case featureTypes.route:
                return MapData.getRouteFromMapFeature(feature);

            default:
                throw new Error("Cannot handle click on feature type: " + type, { cause: feature });
        }

    }

    /**
     * Select a feature on the map from its data type and id.
     *
     * Reproduces a feature selection coming from the URL, where we only have
     * type + id (not the rendered map feature). It queries the currently
     * rendered features by id, builds the proper feature data and selects it.
     *
     * The feature must be rendered in the current viewport.
     *
     * @param {string} dataType One of dataTypes (place, route, stage).
     * @param {int} id Feature id.
     *
     * @return {boolean} Whether a feature was found and selected.
     */
    static selectRenderedFeature(dataType: string, id: string | number): boolean {
        let featureType, features;

        switch (dataType) {
            case dataTypes.place:
                featureType = featureTypes.localidad;
                features = MapService.getCityFeatures(id);
                break;
            case dataTypes.route:
                featureType = featureTypes.route;
                features = MapService.getRouteFeatures(id);
                break;
            case dataTypes.stage:
                featureType = featureTypes.track;
                features = MapService.getTrackFeatures(id);
                break;
            default:
                // Hotels / POIs are DOM markers, not handled here.
                return false;
        }

        if (!features || !features.length) {
            return false;
        }

        const placeData = this.getSelectedFeatureData(featureType, features[0]);
        if (!placeData) {
            return false;
        }

        this.setSelectedPlace(placeData);
        return true;
    }

    /**
     * Highlight the feature referenced by the URL.
     *
     * Each type is highlighted in a way that does not depend on the feature
     * being already painted:
     * - Cities: built from the card geo location (a Point feature).
     * - Hotels / POIs: only the selection id is registered; their DOM marker
     *   is rendered asynchronously when the area loads, and the existing
     *   reload -> showSelected() flow applies the 'selected' class once painted.
     *
     * Routes / stages need their rendered line geometry and are not handled here.
     *
     * @param {PlaceData} placeData
     * @param {CardData} cardData Loaded card for that feature.
     *
     * @return {boolean} Whether the feature type was handled.
     */
    static selectUrlFeature(placeData: PlaceData, cardData: CardData): boolean {
        switch (placeData.type) {
            case dataTypes.place: {
                const location = cardData.getGeoLocation();
                if (!location) {
                    return false;
                }
                const cityData = new CityData(dataTypes.place, placeData.id);
                cityData.setFeature({
                    type: 'Feature',
                    geometry: {type: 'Point', coordinates: [location.lng, location.lat]},
                    properties: {id: placeData.id},
                });
                this.setSelectedPlace(cityData);
                return true;
            }

            case dataTypes.hotel:
                return this.selectMarkerById(this.hotelList, placeData.id);

            case dataTypes.poi:
                return this.selectMarkerById(this.POIList, placeData.id);

            default:
                // Routes / stages need their rendered geometry.
                return false;
        }
    }

    /**
     * Select a DOM-marker feature (hotel / POI) by id.
     *
     * Only the selection id is registered. The marker is rendered
     * asynchronously when the area loads, so the selection may be pending:
     * showSelected() tolerates this (hasSelection() is guarded by
     * dataMap.has(selectedId)) and the reload -> showSelected() flow applies
     * the 'selected' class once the item is loaded and painted.
     *
     * @param {BaseList} list Target list (hotelList / POIList).
     * @param {int} id Feature id.
     *
     * @return {boolean} Always true (the type is handled here).
     */
    static selectMarkerById(list: BaseList<PlaceData>, id: string | number) {
        list.setSelectedId(id);
        this.showSelected();
        return true;
    }

    /**
     * Check last card is loaded.
     */
    static hasCardLoaded(): boolean {
        return !!(this.lastPlaceData && this.lastCardData);
    }

    /**
     * Check place data is the current one.
     * @param {PlaceData} placeData
     */
    static isCurrentPlaceData(placeData: PlaceData): boolean {
        return !!(this.lastPlaceData && this.lastPlaceData.matchFeatureData(placeData));
    }

    /**
     * Get card data from place data.
     *
     * @param {PlaceData|HotelData} placeData
     *
     * @return {CardData} ({Promise})
     */
    static async getCardFromPlaceData(placeData: PlaceData) {
        if (NetworkService.isOffline()) {
            // No network: show a minimal card with the feature title only.
            return OfflineCard.fromFeatureData(placeData);
        }
        if (this.isCurrentPlaceData(placeData)) {
            if (this.lastCardData) {
                this.debug('Card already loaded / showing');
                return this.lastCardData;
            } else {
                this.debug('Last Card not loaded / error. Skip')
                return null;
            }
        } else if (this.selectedCard && placeData.matchCardData(this.selectedCard)) {
            this.debug('Card is selected one, already loaded.');
            return this.selectedCard;
        } else {
            this.lastPlaceData = placeData;
            this.lastCardData = null;
            return DataService.loadFromPlaceData(placeData)
                .then(cardData => {
                    this.lastCardData = cardData ?? null;
                    if (!cardData) {
                        // Failed load (no authorization data yet, no network,
                        // error response). Forget the place so the next attempt
                        // requests it again: otherwise this failure is answered
                        // from the cache for the whole life of the app and the
                        // card only recovers by restarting it.
                        this.lastPlaceData = null;
                    }
                    return cardData;
                }, error => {
                    this.logger().warn('Card data could not be loaded.', error);
                    this.lastPlaceData = null;
                    return null;
                });
        }
    }

    /**
     * Get accommodations for given map bounds. Load when needed.
     *
     * @param {LngLatBounds} bounds Map bounds
     * @return {Promise<[HotelList, POIList]>}
     */
    static async getHotelsAndPOIsForBounds(bounds: LngLatBounds) {
        if (NetworkService.isOffline()) {
            return [this.hotelList, this.POIList];
        }

        if (this.mapBounds && this.mapBounds.containsBounds(bounds)) {
            // New bounds are contained in previous ones, no need to reload.
            this.debug('Contained bounds, no need to reload', bounds);
        }
        else {
            // Expand bounds to load a 10% more
            this.mapBounds = GeoService.expandBounds(bounds, 0.1);
            // Set new bounds and reload hotels.
            const hotelList = await DataService.loadHotels(this.mapBounds);

            if (hotelList) {
                this.debug("Loaded hotels", hotelList.countItems());
                // Replace items, keep selection.
                this.hotelList.cleanData().addPlaceList(hotelList.getDataItems());
            }
            else {
                this.cleanHotelList();
            }

            this.POIList = await this.getPOIsForBounds(bounds);
        }

        return [this.hotelList, this.POIList];
    }


    /**
     * Get POIs for given map bounds. Load when needed.
     *
     * @param {LngLatBounds} bounds Map bounds
     * @return {POIList}
     */
    static async getPOIsForBounds(bounds: LngLatBounds) {
        // Expand bounds to load a 10% more
        this.mapBounds = GeoService.expandBounds(bounds, 0.1);
        // Set new bounds and reload hotels.
        const POIList = await DataService.loadPOIs(this.mapBounds);

        if (POIList) {
            this.debug("Loaded POIs", POIList.countItems());
            // Replace items, keep selection.
            this.POIList.cleanData().addPlaceList(POIList.getDataItems());
        }
        else {
            this.cleanPOIList();
        }

        // Return the persistent list (keeps selection); the caller reassigns
        // this.POIList, so returning the freshly loaded one would drop selectedId.
        return this.POIList;
    }

    /**************************************
     * Map selections: hotels, locations.
     **************************************/

    /**
     * Highlight whatever object is selected.
     */
    static showSelected(): void {
        const cityList = this.cityList;
        const hotelList = this.hotelList;
        const POIList = this.POIList;

        if (cityList.hasSelection()) {
            MapService.setSelectedPlaces(cityList.getSelectedFeatures() as unknown as MapboxGeoJSONFeature[]);
        } else if (hotelList.hasSelection()) {
            hotelList.getSelectedPlace()!.setSelected();
        } else if (POIList.hasSelection()) {
            POIList.getSelectedPlace()!.setSelected();
        }
    }

    /**
     * Set and highlight selected hotel.
     *
     * @param {PlaceData} | null
     */
    static setSelectedHotelOrPOI(placeData?: PlaceData): void {
        this.removeSelectedMarkers();
        this.hotelList.getSelectedPlace()?.setSelected(false);
        this.POIList.getSelectedPlace()?.setSelected(false);
        this.hotelList.cleanSelection();
        this.POIList.cleanSelection();
        if (placeData) {
            this.debug("Selected Hotel", [placeData.id, placeData.name]);
            this.cleanSelection();
            // El tipo del placeData decide en que lista entra; el runtime lo
            // garantiza pero PlaceData es el tipo base de ambas.
            if (placeData?.type === dataTypes.hotel) {
                this.hotelList.setSelectedPlace(placeData as HotelData);
            } else if (placeData?.type === dataTypes.poi) {
                this.POIList.setSelectedPlace(placeData as POIData);
            }
            this.showSelected();
        }
    }

    /**
     * Set and highlight selected location
     *
     * @param {PlaceData} [placeData] Sin argumento, limpia la seleccion.
     */
    static setSelectedCity(placeData?: FeatureData): void {
        if (placeData) {
            this.debug("Selected City", [placeData.id, placeData.name]);
            this.cleanSelection();
            this.cityList.setSelectedPlace(placeData as CityData);
            // Temporary, check size every time.
            this.cityList.checkSize();
            this.showSelected();
        } else if (this.cityList.hasSelection()) {
            this.cityList.cleanSelection();
            MapService.setSelectedPlaces([]);
        }
    }

    /**
     * Set seleted place, generic.
     *
     * @param {PlaceData}
     */
    static setSelectedPlace(placeData: FeatureData): void {
        // If missing map_id, skip
        const map_id = placeData.getMapItemId();

        if (!map_id) return;

        // clean before draw
        MapService.cleanSelectedFeatures();

        switch (placeData.type) {
            case dataTypes.place:
                this.setSelectedCity(placeData);
                break;
            case dataTypes.hotel:
                this.setSelectedHotelOrPOI(placeData as PlaceData);
                break
            case dataTypes.route:
                MapService.setSelectedRouteId(map_id);
                break;
            case dataTypes.stage:
                MapService.setSelectedTrackId(map_id);
                break;
            case dataTypes.poi:
                this.setSelectedHotelOrPOI(placeData as PlaceData);
                break
            default:
            // No action.
        }
    }

    /**
     * Set current card, that is displayed.
     *
     * @param {CardData} cardData
     *
     * @todo Extend for other card types.
     */
    static setCurrentCard(cardData: CardData): void {
        switch (cardData.type) {
            case dataTypes.stage:
                MapService.setSelectedTrackId(cardData.getMapId() ?? null);
                break;
            default:
            // Stupid linter needs a default case.
        }
    }

    /**
     * Set selected card.
     *
     * @param {CardData} cardData
     *
     * @todo Extend for other card types.
     */
    static setSelectedCard(cardData: CardData): void {
        cardData.selected = true;

        this.selectedCard = cardData;

        switch (cardData.type) {
            case dataTypes.stage:
                MapService.setCurrentTrackId(cardData);
                break;

            default:
                this.logger().warn("Uhandled selected card type", cardData);

        }
    }

    /**
     * Clean all selected items.
     */
    static cleanSelection(): void {
        this.setSelectedCity();
        this.setSelectedHotelOrPOI();
        MapService.cleanSelectedFeatures();
    }

    /**
     * Clean Hotel list, reset bounds.
     */
    static cleanHotelList(): void {
        this.hotelList.clean();
        this.mapBounds = null;
    }

    /**
     * Clean POI list, reset bounds.
     */
    static cleanPOIList(): void {
        this.POIList.clean();
        this.mapBounds = null;
    }

    /**
     * Clean Hotel list, reset bounds.
     */
    static cleanHotelMarkers(keep_selected: boolean): void {
        if (!keep_selected && this.hotelList.hasSelection()) {
            this.hotelList.getSelectedPlace()?.setSelected(false);
        }
        const keep = keep_selected ? this.hotelList.getSelectedPlace() : null;
        this.hotelList.getDataItems().forEach(item => {
            if (!keep || item.id !== keep.id ) {
                item.removeMarker();
            }
        })
    }

    /**
     * Clean POI list, reset bounds.
     */
    static cleanPOIMarkers(keep_selected: boolean): void {
        if (!keep_selected && this.POIList.hasSelection()) {
            this.POIList.getSelectedPlace()?.setSelected(false);
        }
        const keep = keep_selected ? this.POIList.getSelectedPlace() : null;
        this.POIList.getDataItems().forEach(item => {
            if (!keep || item.id !== keep.id ) {
                item.removeMarker();
            }
        })
    }

    /**
     * Remove the 'selected' class from selected hotel and POI markers.
     */
    static removeSelectedMarkers(): void {
        if (this.hotelList.hasSelection()) {
            const selectedHotel = this.hotelList.getSelectedPlace();
            if (selectedHotel?.htmlElement) {
                selectedHotel.htmlElement.classList.remove('selected');
            }
        }
        if (this.POIList.hasSelection()) {
            const selectedPOI = this.POIList.getSelectedPlace();
            if (selectedPOI?.htmlElement) {
                selectedPOI.htmlElement.classList.remove('selected');
            }
        }
        MapService.removeSelectedPlaces();
    }
}

export default MapData;
